import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Typography,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AttachedFiles,
  BudgetItemTypeEnum,
  CalcOption,
  CategoryTotals,
  initCategoryTotals,
  IPaymentSolicitationByType,
  MainAppliedTaxes,
} from "../../../../types/budget/budget";
import { EventScopeEnum } from "../../../../types/project/enums";
import { useLocation } from "react-router-dom";
import { BiChevronDown } from "react-icons/bi";
import { useProject } from "../../../../contexts/project.context";
import React, { useEffect, useMemo, useState } from "react";
import { ServiceValues } from "../../../../types/budget/service-item";
import { toast } from "react-toastify";
import { createOptionsFromEnum } from "../../../../utils/formatters";
import { BudgetIcons } from "../../../../assets/styled-icons/budget";
import { HiOutlinePlus } from "react-icons/hi";
import { IoIosCloseCircle } from "react-icons/io";
import { MdDragIndicator } from "react-icons/md";
import ChangelingMonetaryInput from "../../../UI/ChangelingMonetaryInput";
import ExchangeFields from "../../../UI/ExchangeFields";
import { LabeledInput } from "../../../UI/LabeledInput";
import { currencyFormatter } from "../../../../utils/currencyFormatter";
import CoinData from "../../../../utils/Common-Currency.json";
import { getCurrencyDisplayLabel } from "../../../../utils/currencyDisplay";
import { sortCurrencyOptions } from "../../../../utils/currencySort";
import { TabActive } from "../../../../types/project.type";
import { BudgetService } from "../../../../services/budget.service";
import { BudgetItemIcon } from "../../Budget/components/BudgetItemIcon";
import AppliedTaxes from "../AppliedTaxes";
import BaseValuesTotal from "../../Budget/BaseValuesTotal";
import { defineTotals } from "./methods";
import BaseMonetaryData from "../BaseMonetaryData";
import PaymentMethodInfo from "../PaymentMethodInfo";
import { useServices } from "../../../../contexts/ServicesContext/projectservices.context";
import ServiceGroupItem from "../ServiceGroupItem";
import { getEventExchange } from "../../../../utils/exchangeManager";
import {
  dispatchAddService,
  isAddServicePointerGestureLocked,
  prepareAddServiceFromPointer,
} from "../../../../utils/add-service-gesture";

type Props = {
  type: keyof typeof BudgetItemTypeEnum;
  supplierId: string;
  supplierName: string;
  paymentInfo: IPaymentSolicitationByType;
  panel: string;
  baseValuePanel: string;
  expanded: boolean;
  baseValueExpanded: boolean;
  onRemoveItem: (type: keyof typeof BudgetItemTypeEnum) => void;
  onChange: (
    panel: string
  ) => (_event: React.SyntheticEvent, isExpanded: boolean) => void;
  index: number;
  onChangeBaseValue: (
    panel: string
  ) => (_event: React.SyntheticEvent, isExpanded: boolean) => void;
};

const coinOptions = sortCurrencyOptions(
  CoinData.map((el) => ({
    value: el.code,
    label: getCurrencyDisplayLabel(el.name),
  }))
);

// Sortable wrapper component for each service item
const SortableServiceItem = ({
  service,
  index,
  type,
  baseValueExchange,
  descriptionFocus,
  setDescriptionFocus,
  serviceFocus,
  setServiceFocus,
  hoveredServiceIndex,
  setHoveredServiceIndex,
  handleAddServiceAtPosition,
  disabled,
  mainAppliedTaxes,
  exchangeCurrency,
  categoryExchange,
}: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: service._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onMouseEnter={() => {
        if (isAddServicePointerGestureLocked()) return;
        setHoveredServiceIndex(index);
      }}
      onMouseLeave={() => {
        if (isAddServicePointerGestureLocked()) return;
        setHoveredServiceIndex(null);
      }}
      sx={{ position: "relative" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          border: isDragging ? "2px dashed #1361A4" : "1px solid transparent",
          borderRadius: 1,
          padding: isDragging ? 1 : 0,
          backgroundColor: isDragging ? "#f0f7ff" : "transparent",
        }}
      >
        {!disabled && (
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: "grab",
              "&:active": { cursor: "grabbing" },
              display: "flex",
              alignItems: "center",
              padding: "8px",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
              },
            }}
          >
            <MdDragIndicator size={24} color="#666" />
          </Box>
        )}
        <Box flex={1}>
          <ServiceGroupItem
            type={type}
            index={index}
            title={BudgetItemTypeEnum[type as keyof typeof BudgetItemTypeEnum]}
            serviceData={service}
            currency={exchangeCurrency || "BRL"}
            categoryExchange={categoryExchange}
            descriptionFocus={descriptionFocus}
            setDescriptionFocus={setDescriptionFocus}
            serviceFocus={serviceFocus}
            setServiceFocus={setServiceFocus}
            mainAppliedTaxes={mainAppliedTaxes}
          />
        </Box>
      </Box>
      {hoveredServiceIndex === index && !disabled && (
        <Divider
          sx={{
            cursor: "pointer",
            color: "#1361A4",
            fontSize: "13px",
            "&:hover": {
              color: "#f0f7ff",
            },
            transition: "all 0.2s ease",
          }}
          onMouseDown={(event) => {
            if (!prepareAddServiceFromPointer(event)) return;
            handleAddServiceAtPosition(index + 1);
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <Chip
            component="span"
            label="+ Novo serviço"
            color="primary"
            size="small"
          />
        </Divider>
      )}
    </Box>
  );
};

const ServiceGroup: React.FC<Props> = ({
  type,
  supplierId,
  paymentInfo,
  supplierName,
  expanded,
  onChange,
  onRemoveItem,
  panel,
  index,
  onChangeBaseValue,
  baseValuePanel,
  baseValueExpanded,
}: Props) => {
  const [totals, setTotals] = useState<CategoryTotals>(
    JSON.parse(JSON.stringify(initCategoryTotals))
  );
  const [comparison, setComparison] = useState<CategoryTotals>(
    JSON.parse(JSON.stringify(initCategoryTotals))
  );

  const { activeService, activeServiceDispatch, activeCalcOption } =
    useServices();
  const { tabActive, event, setTabActive } = useProject();
  // const itemTotal = activeBudget?.budgetValuesTotal?.[type]?.totalInBRL || 0;
  // uma lista com os serviços que são do tipo e fornecedor recebidos

  const baseValue = activeService?.baseValues?.find(
    (el) =>
      el.calcOption === activeCalcOption &&
      el.supplierId === supplierId &&
      el.category === type
  );

  const baseValueExchange = baseValue?.exchange;

  /** Nacional não exibe Câmbio na categoria; baseValues podem manter moeda antiga até sync — para UI fiscal usa-se BRL. */
  const effectiveCategoryExchange = useMemo(() => {
    if (event?.eventScope === EventScopeEnum.NATIONAL) {
      return { date: "", currency: "BRL" as const, value: 1 };
    }
    return (
      baseValue?.exchange || {
        currency: "BRL" as const,
        date: "",
        value: 1,
      }
    );
  }, [event?.eventScope, baseValue?.exchange]);

  useEffect(() => {
    if (!baseValue) {
      // Obtém o câmbio do evento para replicar na categoria
      const eventExchange = getEventExchange(event);

      activeServiceDispatch({
        type: "INIT_BASE_VALUES",
        payload: {
          supplierId,
          type,
          calcOption: activeCalcOption,
          eventExchange, // Passa o câmbio do evento (ou null se Nacional)
        },
      });
    }
  }, [baseValue, supplierId, type, activeCalcOption]); // Removido 'event' para evitar loop

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  // Memoized filtered services list for this type/supplier/calcOption
  const filteredServicesList = useMemo(() => {
    return (
      activeService?.budgetServices?.filter(
        (el) =>
          el.type === type &&
          el.supplierId === supplierId &&
          el.calcOption === activeCalcOption
      ) || []
    );
  }, [
    activeService?.budgetServices,
    type,
    supplierId,
    activeCalcOption,
    tabActive,
  ]);

  function handleAddNewService() {
    dispatchAddService(
      activeServiceDispatch,
      {
        type: "ADD_SERVICE",
        payload: {
          supplierId,
          supplierName,
          type,
          tabActive,
          calcOption: activeCalcOption,
        },
      },
      { holdUntilFlush: false }
    );
  }

  /**
   * Calculates the position in the full budgetServices array that corresponds
   * to a specific position in the filtered list.
   *
   * @param positionInFilteredList - The index in the filtered services list (0-based)
   * @returns The corresponding index in the full budgetServices array, or -1 if at the end
   */
  function calculateFullArrayPosition(positionInFilteredList: number): number {
    // If adding at the end or beyond
    if (positionInFilteredList >= filteredServicesList.length) {
      return -1; // Signal to add at the end
    }

    // Find the target service at the desired position in filtered list
    const targetServiceInFiltered =
      filteredServicesList[positionInFilteredList];

    // Find its index in the full budgetServices array
    const fullArrayIndex = activeService?.budgetServices?.findIndex(
      (service) => service === targetServiceInFiltered
    );

    return fullArrayIndex !== undefined ? fullArrayIndex : -1;
  }

  /**
   * Adds a service at a specific position in the filtered list.
   * Automatically calculates the corresponding position in the full budgetServices array.
   *
   * @param positionInFilteredList - The index where to insert in the filtered services list (0-based)
   */
  function handleAddServiceAtPosition(positionInFilteredList: number) {
    const fullArrayPosition = calculateFullArrayPosition(
      positionInFilteredList
    );
    dispatchAddService(
      activeServiceDispatch,
      {
        type: "ADD_SERVICE",
        payload: {
          supplierId,
          supplierName,
          type,
          tabActive,
          calcOption: activeCalcOption,
          insertAtIndex: fullArrayPosition >= 0 ? fullArrayPosition : undefined,
        },
      },
      { holdUntilFlush: false }
    );
  }

  function defineFiles() {
    const files = activeService.files.find(
      (el) =>
        el.calcOption === activeCalcOption &&
        el.supplierId === supplierId &&
        el.type === type
    );
    return files || ({} as AttachedFiles);
  }

  useMemo(() => {
    if (activeService?.budgetServices) {
      const { calcTotalsSource, calcTotalsCompare }: any = defineTotals(
        activeCalcOption,
        activeService,
        supplierId,
        type
      );
      setTotals(calcTotalsSource);
      setComparison(calcTotalsCompare);
    }
  }, [activeService]);

  const mainAppliedTaxes = useMemo(() => {
    const savedMainAppliedTaxes = activeService?.mainAppliedTaxes?.find(
      (el) =>
        el.calcOption === activeCalcOption &&
        el.supplierId === supplierId &&
        el.category === type
    );
    if (!savedMainAppliedTaxes) {
      activeServiceDispatch({
        type: "INIT_MAIN_APPLIED_TAXES",
        payload: {
          supplierId,
          category: type,
          calcOption: activeCalcOption,
          version: 0,
        },
      });
    }
    return (
      savedMainAppliedTaxes ||
      ({
        appliedTaxes: {
          serviceTax: true,
          iss: true,
          cbs: true,
          ibs: true,
          tribute: true,
          supplier_tribute: true,
          localTax: true,
          tourismTax: true,
          fee: true,
          iof: true,
          markup: true,
          overhead: true,
          commission: true,
          incentive: true,
          doubleCharged: true,
        },
        calcOption: activeCalcOption,
        supplierId,
        category: type,
        version: 0,
      } as MainAppliedTaxes)
    );
  }, [activeService, activeCalcOption, supplierId, type]);

  const totalsSummary = useMemo(() => {
    return [
      { title: "T. Tarifas", value: totals.tariffTotals },
      { title: "T. Taxas", value: totals.serviceTaxTotals },
      { title: "T. Receita", value: totals.revenueTotals },
      { title: "T. Imposto", value: totals.taxes },
      { title: "T. Geral", value: totals.totals },
    ];
  }, [totals]);

  function handleUpdateBaseMonetary(value: any, field: any) {
    const updatedBaseValue = { ...baseValue };

    let newBaseValues = updatedBaseValue;
    newBaseValues.taxData = value;

    activeServiceDispatch({
      type: "UPDATE_BASE_CALC_VALUES",
      payload: {
        value,
        supplierId,
        type,
        calcOption: activeCalcOption,
        field: "taxData",
      },
    });
  }

  function updateExchangeValues(
    newType: string,
    newValue: number,
    calcType: string
  ) {
    const eventExchange = getEventExchange(event);

    // Se a moeda mudou para a moeda do evento, aplica os valores do evento
    if (eventExchange && calcType === eventExchange.currency) {
      activeServiceDispatch({
        type: "UPDATE_BASE_CALC_VALUES",
        payload: {
          value: {
            currency: eventExchange.currency,
            value: eventExchange.value,
            date: eventExchange.date,
            isOverride: false, // Remove override ao voltar para moeda do evento
          },
          supplierId,
          type,
          calcOption: activeCalcOption,
          field: "exchange",
        },
      });
      toast.success("Câmbio sincronizado com Dados Cadastrais do Evento!");
    } else {
      // Moeda diferente do evento: mantém valor manual e marca override
      activeServiceDispatch({
        type: "UPDATE_BASE_CALC_VALUES",
        payload: {
          value: {
            currency: calcType,
            value: newValue,
            date: baseValueExchange?.date,
          },
          supplierId,
          type,
          calcOption: activeCalcOption,
          field: "exchange",
        },
      });
    }
  }

  function updateExchangeDate(date: string) {
    // Verifica se o valor realmente mudou antes de disparar o dispatch
    if (baseValueExchange?.date === date) {
      // Valor não mudou, não precisa disparar atualização
      return;
    }

    activeServiceDispatch({
      type: "UPDATE_BASE_CALC_VALUES",
      payload: {
        value: {
          currency: baseValueExchange?.currency,
          value: baseValueExchange?.value,
          date: date,
        },
        supplierId,
        type,
        calcOption: activeCalcOption,
        field: "exchange",
      },
    });
  }

  function resetExchangeToDefault() {
    const eventExchange = getEventExchange(event);
    if (eventExchange) {
      activeServiceDispatch({
        type: "UPDATE_BASE_CALC_VALUES",
        payload: {
          value: {
            ...eventExchange,
            isOverride: false,
          },
          supplierId,
          type,
          calcOption: activeCalcOption,
          field: "exchange",
        },
      });
      toast.success("Câmbio restaurado para o padrão do evento!");
    }
  }

  // Função para navegar até Dados Cadastrais - Dados do Evento
  function handleNavigateToEvent() {
    if (setTabActive) {
      // PRIMEIRO: Adiciona o parâmetro na URL ANTES de mudar a aba
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set("subTab", "event");
      window.history.replaceState({}, "", currentUrl.toString());
      // SEGUNDO: Muda para a aba Dados Cadastrais
      // O ProjectStep vai ler o parâmetro ao ser renderizado
      setTabActive(TabActive.INITIAL);

      toast.info("Navegando para Dados do Evento");
    }
  }

  const [descriptionFocus, setDescriptionFocus] = useState(null);
  const [serviceFocus, setServiceFocus] = useState(null);
  const [hoveredServiceIndex, setHoveredServiceIndex] = useState<number | null>(
    null
  );

  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require dragging 8px before activating
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Find the old and new indices in the filtered list
      const oldIndexFiltered = filteredServicesList.findIndex(
        (service) => service._id === active.id
      );
      const newIndexFiltered = filteredServicesList.findIndex(
        (service) => service._id === over.id
      );

      // Map to full array indices
      const oldServiceInFull = filteredServicesList[oldIndexFiltered];
      const newServiceInFull = filteredServicesList[newIndexFiltered];

      const oldIndexFull = activeService?.budgetServices?.findIndex(
        (service) => service === oldServiceInFull
      );
      const newIndexFull = activeService?.budgetServices?.findIndex(
        (service) => service === newServiceInFull
      );

      if (
        oldIndexFull !== undefined &&
        newIndexFull !== undefined &&
        oldIndexFull >= 0 &&
        newIndexFull >= 0
      ) {
        activeServiceDispatch({
          type: "REORDER_SERVICES",
          payload: {
            oldIndex: oldIndexFull,
            newIndex: newIndexFull,
          },
        });
      }
    }
  };

  return (
    <Accordion
      sx={{
        my: 2,
        borderRadius: "4px",
        border: "none",
      }}
      expanded={expanded}
      defaultExpanded={true}
      onChange={onChange(panel)}
      TransitionProps={{ unmountOnExit: false }}
    >
      <AccordionSummary
        aria-controls="panel1a-content"
        expandIcon={<BiChevronDown color="white" />}
        id="panel1a-header"
        style={{
          backgroundColor: "#3B668B",
          borderBottom: "1px solid #eee",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            flexDirection: "row",
            display: "flex",
            alignItems: "center",
            gap: 2,
            marginRight: 8,
          }}
        >
          {/* <MdDragIndicator color="#555555" /> */}
          <span style={{ color: "white" }}>{index + 1}</span>
        </div>
        <div
          style={{
            width: 1,
            backgroundColor: "rgba(255,255,255,0.1)",
            marginRight: 8,
            marginLeft: 8,
          }}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            justifyContent: "space-between",
            flex: 1,
          }}
        >
          <Box style={{ alignItems: "center", display: "flex" }} flex={0.3}>
            <BudgetItemIcon color="white" icon={BudgetIcons[type]} />
            <span style={{ fontWeight: "bold", marginLeft: 4, color: "white" }}>
              {BudgetItemTypeEnum[type]}
            </span>
          </Box>

          <Box
            display="flex"
            flex={0.7}
            justifyContent={"space-evenly"}
            gap={1}
          >
            {totalsSummary.map((i, index) => (
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
                flex={1}
              >
                <span
                  style={{
                    color: "white",
                    fontSize: 14,
                  }}
                >
                  {i.title}
                </span>
                <span style={{ fontSize: 14, color: "white", fontWeight: 500 }}>
                  {currencyFormatter.format(i.value.totalInBRL)}
                </span>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              backgroundColor: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "100px",
              px: 1.5,
              py: 0.25,
              ml: 1,
              minWidth: 48,
              justifyContent: "center",
            }}
          >
            <span
              style={{
                color: "white",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.5px",
                lineHeight: 1.5,
                whiteSpace: "nowrap",
              }}
            >
              {baseValueExchange?.currency || "BRL"}
            </span>
          </Box>
        </div>

        {/* <Box ml={"auto"} mr={1}>
          <ItemTotalsSummary total={totals.totals} savings={totals.savings} />
        </Box> */}
      </AccordionSummary>
      <Box>{/* total summary */}</Box>

      <AccordionDetails style={{ backgroundColor: "white" }}>
        <Box
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"center"}
          gap={1}
        >
          {activeService?.budgetServices?.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredServicesList
                  .map((service) => service._id)
                  .filter((id): id is string => !!id)}
                strategy={verticalListSortingStrategy}
              >
                {filteredServicesList.map((service: any, index: number) => {
                  return (
                    <SortableServiceItem
                      key={service._id || index}
                      service={service}
                      index={index}
                      type={type}
                      baseValueExchange={baseValueExchange}
                      exchangeCurrency={effectiveCategoryExchange.currency}
                      categoryExchange={effectiveCategoryExchange}
                      descriptionFocus={descriptionFocus}
                      serviceFocus={serviceFocus}
                      setDescriptionFocus={setDescriptionFocus}
                      setServiceFocus={setServiceFocus}
                      hoveredServiceIndex={hoveredServiceIndex}
                      setHoveredServiceIndex={setHoveredServiceIndex}
                      handleAddServiceAtPosition={handleAddServiceAtPosition}
                      disabled={false}
                      mainAppliedTaxes={mainAppliedTaxes?.appliedTaxes}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          ) : (
            <Box margin={"auto"}>
              <h4>Nenhum serviço salvo para {BudgetItemTypeEnum[type]} </h4>
            </Box>
          )}

          <Button
            sx={{
              ml: "auto",
              my: 2,
              width: "100%",
              borderWidth: "1px",
              borderColor: "#1361A4",
              borderStyle: "dashed",
              backgroundColor: "white",
            }}
            onMouseDown={(event) => {
              if (!prepareAddServiceFromPointer(event)) return;
              handleAddNewService();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (event.detail !== 0) return;
              handleAddNewService();
            }}
            variant="outlined"
            startIcon={<HiOutlinePlus />}
            disabled={false}
          >
            Adicionar serviço
          </Button>
        </Box>

        <AppliedTaxes
          calcOption={activeCalcOption as CalcOption}
          category={type}
          supplierId={supplierId}
          exchangeCurrency={effectiveCategoryExchange.currency}
          eventScope={event?.eventScope}
        />
        <Accordion
          sx={{
            my: 2,
            borderRadius: "8px",
            border: "1px solid #1361A4",
            boxShadow: "none",
            overflow: "hidden",
          }}
          expanded={baseValueExpanded}
          defaultExpanded={true}
          onChange={onChangeBaseValue(baseValuePanel)}
        >
          <AccordionSummary
            aria-controls="panel1a-content"
            expandIcon={<BiChevronDown />}
            id="panel1a-header"
            style={{
              backgroundColor: "#FAFDFF",
              borderBottom: "1px solid #eee",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box>
              <span style={{ color: "#1361A4", fontWeight: 600 }}>Valores</span>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Defina valores, taxas e tarifas para esta categoria.
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails style={{ backgroundColor: "white" }}>
            <BaseMonetaryData
              taxData={baseValue?.taxData}
              // compareTaxData={compareServiceValues?.taxData}
              exchange={effectiveCategoryExchange}
              compareExchange={{
                currency: "BRL",
                date: "",
                value: 1,
              }}
              onChange={handleUpdateBaseMonetary}
              itemType={type}
              totals={totals}
            />

            {(event?.eventScope === EventScopeEnum.INTERNATIONAL ||
              event?.eventScope === EventScopeEnum.BOTH) && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid #E3E3E3",
                  padding: 16,
                  borderRadius: 8,
                  marginTop: 8,
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                >
                  <span style={{ fontWeight: "bold", fontSize: "14px" }}>
                    Câmbio
                  </span>
                  {baseValue?.isOverride && (
                    <Chip
                      label="Personalizado"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>

                <ExchangeFields
                  date={baseValueExchange?.date || ""}
                  currency={baseValueExchange?.currency || "BRL"}
                  value={baseValueExchange?.value || 1}
                  onDateChange={updateExchangeDate}
                  onExchangeChange={(currency, value) =>
                    updateExchangeValues("", value, currency)
                  }
                  disabled={false}
                  showTitle={false}
                  eventCurrency={event?.exchangeCurrency}
                  showSyncMessage={true}
                  onNavigateToEvent={handleNavigateToEvent}
                />

                {baseValue?.isOverride && (
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button
                      onClick={resetExchangeToDefault}
                      variant="outlined"
                      size="small"
                      color="secondary"
                    >
                      Restaurar Padrão do Evento
                    </Button>
                  </Box>
                )}
              </div>
            )}

            <BaseValuesTotal
              totals={totals}
              showExtras={false}
              displayCurrencyCode={effectiveCategoryExchange.currency}
            />
          </AccordionDetails>
        </Accordion>

        <Box display={"flex"} justifyContent={"flex-end"}>
          <Button
            sx={{
              border: "1px solid #D32F2F",
              color: "white",
              cursor: "pointer",
              background: "#D32F2F",
              "&:hover": {
                border: "1px solid #D32F2F",
                background: "#D32F2F",
              },
              p: 0.62,
              height: "100%",
            }}
            variant="outlined"
            onClick={(e) => {
              onRemoveItem(type);
            }}
            startIcon={<IoIosCloseCircle style={{ color: "white" }} />}
            disabled={false}
          >
            Remover {BudgetItemTypeEnum[type]}
          </Button>
          {/* <Button
            sx={{ ml: "10px" }}
            variant="outlined"
            onClick={() => {
              if (activeBudget._id) {
                updateBudget(activeBudget._id, activeBudget, "SAVE", -1);
                toast.success("Salvo com sucesso!");
              }
            }}
          >
            Salvar
          </Button> */}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default ServiceGroup;
